// frontend/webapp/src/hooks/useHolidayCatalog.ts
//
// Fetches the holiday catalog filtered by the user's country_codes.
// Direct table read via RLS (which grants SELECT to authenticated);
// no RPC. Cache: 24h staleTime, 7d gcTime (catalog only changes
// monthly via the edge function).

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { CatalogHoliday } from '@/types/holiday';
import { useUserConfigState } from '@/context/UserConfigContext';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

async function fetchCatalog(countryCodes: string[]): Promise<CatalogHoliday[]> {
  if (countryCodes.length === 0) return [];

  const { data, error } = await supabase
    .from('holidays')
    .select('id, code, country_code, name, local_name, types, is_global, counties')
    .in('country_code', countryCodes)
    .order('country_code', { ascending: true })
    .order('name', { ascending: true });

  if (error) throw error;

  return (data ?? []).map(row => ({
    id: row.id,
    code: row.code,
    countryCode: row.country_code,
    name: row.name,
    localName: row.local_name,
    types: row.types ?? [],
    isGlobal: row.is_global,
    counties: row.counties ?? undefined,
  }));
}

export function useHolidayCatalog() {
  const { countryCodes } = useUserConfigState();

  return useQuery<CatalogHoliday[], Error>({
    queryKey: ['holiday-catalog', countryCodes],
    queryFn: () => fetchCatalog(countryCodes),
    staleTime: TWENTY_FOUR_HOURS_MS,
    gcTime: SEVEN_DAYS_MS,
  });
}
